import type { ActivitySummary } from "@contract/firm/activity";
import { SimpleGrid, Stack, Text } from "@mantine/core";
import * as React from "react";
import { fetchActivitySummary } from "../../api/activity";
import { SectionCard } from "../../ui/SectionCard";
import { PANEL_SPACE } from "../../ui/tokens";
import { monthStart } from "../homeModel";

/** One figure with its label under it. */
function Stat({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <Stack gap={2}>
      <Text fz={20} fw={600} lh={1.2}>
        {value}
      </Text>
      <Text c="dimmed" lh={1.25}>
        {label}
      </Text>
    </Stack>
  );
}

/**
 * The "This Month" strip (firm-model spec §10.2, D73; `view_activity`; on Activity Log since
 * Dan 2026-10-02, first on Home): runs finished,
 * issues fixed and the most active members since the 1st, from the activity summary. A firm
 * of one gets no "most active". It renders nothing until the summary arrives, and nothing if
 * the read fails (Home never waits on it).
 */
export function ThisMonthCard({ now, showMembers, load = fetchActivitySummary }: { now: Date; showMembers: boolean; load?: typeof fetchActivitySummary }) {
  const [summary, setSummary] = React.useState<ActivitySummary | null>(null);
  const from = monthStart(now);
  React.useEffect(() => {
    let alive = true;
    load({ from })
      .then((s) => alive && setSummary(s))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [from]);
  if (!summary) return null;

  const top = summary.topMembers.map((m) => `${m.email ?? m.name ?? "A former member"} (${m.runs})`).join(", ");
  return (
    <SectionCard title="This Month">
      <SimpleGrid cols={{ base: 1, sm: showMembers ? 3 : 2 }} spacing={PANEL_SPACE.page}>
        <Stat value={summary.runs.toLocaleString("en-US")} label={summary.runs === 1 ? "Run" : "Runs"} />
        <Stat value={summary.issuesFixed.toLocaleString("en-US")} label={summary.issuesFixed === 1 ? "Issue fixed" : "Issues fixed"} />
        {showMembers ? (
          <Stack gap={2}>
            <Text fw={600} lh={1.25}>
              Most active
            </Text>
            <Text c={top ? undefined : "dimmed"} lh={1.25}>
              {top || "No runs yet this month."}
            </Text>
          </Stack>
        ) : null}
      </SimpleGrid>
    </SectionCard>
  );
}
